import * as THREE from 'three';
import { withFogOfWar } from '../environment/fog-of-war.js';

/**
 * The smoke a Shadow's body is wrapped in: soft dark puffs scattered over each part of it (the trunk, the head, every arm segment), parented to
 * that part so the smoke bends and swings with it. They churn and swell in their shader and hide the hard edges of the shapes underneath.
 */
export interface SmokeUniforms {
  uTime: { value: number };
  /** 0..1: the Shadow's fade, rising and dissolving. */
  uFade: { value: number };
  /** How thick the smoke is (1 at rest); the animator thickens it as it gathers and strikes. */
  uDensity: { value: number };
  /** Puff size to pixels, set before each draw from the camera and the figure's scale. */
  uScale: { value: number };
  uOrtho: { value: number };
}

const VERTEX = /* glsl */ `
  #include <common>
  #include <fog_pars_vertex>
  uniform float uTime; uniform float uScale; uniform float uOrtho; uniform float uDensity;
  attribute vec4 aPuff;
  varying float vSeed; varying float vFree;
  void main() {
    float size = aPuff.x, seed = aPuff.y, phase = aPuff.z, free = aPuff.w;
    vec3 p = position;
    // Each puff wanders round its place and the loose ones drift up and off, so the smoke never sits still.
    p += vec3(sin(uTime * (.7 + seed) + phase), cos(uTime * (.9 + seed * .5) + phase * 1.3), sin(uTime * (.6 + seed * .7) + phase * 2.1)) * (.012 + .03 * free);
    p.y += fract(uTime * (.12 + .1 * seed) + seed) * .07 * free;
    vec4 mvPosition = modelViewMatrix * vec4(p, 1.);
    float swell = (.85 + .25 * sin(uTime * (1.1 + seed) + phase * 3.)) * (.8 + .3 * uDensity);
    gl_PointSize = size * swell * uScale / mix(-mvPosition.z, 1., uOrtho);
    gl_Position = projectionMatrix * mvPosition;
    vSeed = seed; vFree = free;
    #include <fog_vertex>
  }`;
const FRAGMENT = /* glsl */ `
  #include <common>
  #include <fog_pars_fragment>
  uniform float uTime; uniform float uFade; uniform float uDensity; varying float vSeed; varying float vFree;
  float puHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float puNoise(vec2 p) {
    vec2 i = floor(p), f = fract(p); f = f * f * (3. - 2. * f);
    return mix(mix(puHash(i), puHash(i + vec2(1, 0)), f.x), mix(puHash(i + vec2(0, 1)), puHash(i + vec2(1, 1)), f.x), f.y);
  }
  void main() {
    vec2 c = gl_PointCoord * 2. - 1.;
    float r = length(c);
    // Torn, curling smoke rather than a round dot.
    float n = puNoise(c * 2.2 + vec2(vSeed * 17., uTime * .5)) * .6 + puNoise(c * 4.7 - vec2(uTime * .3, vSeed * 9.)) * .4;
    float alpha = (1. - smoothstep(.15, 1., r + (n - .5) * .55)) * (.6 + .4 * n) * uFade * min(1.2, uDensity) * (.9 - .35 * vFree);
    if (alpha < .01) discard;
    gl_FragColor = vec4(vec3(.012, .011, .016) + vec3(.02, .02, .028) * (1. - n), min(1., alpha));
    #include <fog_fragment>
  }`;

/** `samples` holds, per puff, its place (x, y, z in the part's frame), size (model units), seed, phase and how loose it is (0..1). */
export function createSmokeHull(samples: readonly number[], uniforms: SmokeUniforms): THREE.Points {
  const count = samples.length / 7, positions = new Float32Array(count * 3), puffs = new Float32Array(count * 4);
  for (let i = 0; i < count; i++) {
    positions.set(samples.slice(i * 7, i * 7 + 3), i * 3);
    puffs.set(samples.slice(i * 7 + 3, i * 7 + 7), i * 4);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3)); geometry.setAttribute('aPuff', new THREE.BufferAttribute(puffs, 4));
  geometry.computeBoundingSphere(); geometry.boundingSphere!.radius += .3;
  const material = withFogOfWar(new THREE.ShaderMaterial({
    vertexShader: VERTEX, fragmentShader: FRAGMENT, uniforms: { ...THREE.UniformsUtils.clone(THREE.UniformsLib.fog), ...uniforms },
    transparent: true, depthWrite: false, fog: true,
  }));
  material.name = 'shadow-smoke-hull';
  const points = new THREE.Points(geometry, material); points.name = 'shadow-smoke-hull'; points.renderOrder = 3;
  points.raycast = () => {};
  const size = new THREE.Vector2(), scale = new THREE.Vector3();
  points.onBeforeRender = (renderer, _scene, camera) => {
    renderer.getDrawingBufferSize(size);
    const ortho = (camera as THREE.OrthographicCamera).isOrthographicCamera === true;
    uniforms.uScale.value = size.y * .5 * camera.projectionMatrix.elements[5] * scale.setFromMatrixScale(points.matrixWorld).y;
    uniforms.uOrtho.value = ortho ? 1 : 0;
  };
  return points;
}
